import React, { ComponentProps, useState } from 'react'
import { LinkProps } from 'react-router-dom'
import isEmail from 'validator/lib/isEmail'
import classNames from 'classnames'
import { isEmpty } from 'lodash-es'

import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import ListItem from '@material-ui/core/ListItem'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'

import { ContestGoalTypeToken } from 'lib/enumTranslations'
import { IContest, ContestStatus } from 'protos'

import T from 'components/FormattedMessage'
import Paper from 'components/Paper'
import Button from 'components/Button'
import Ribbon from 'components/Ribbon'
import EventDetailsHeader from 'components/EventDetailsHeader'

import RankLabel from './RankLabel'
import EventLink from './EventLink'
import ScoreLabel from './ScoreLabel'
import PrizeLabel from './PrizeLabel'
import TopPrizeLabel from './TopPrizeLabel'
import FinishedLabel from './FinishedLabel'
import CountdownLabel from './CountdownLabel'

type RibbonProps = React.ComponentProps<typeof Ribbon>

type Props = Omit<ComponentProps<typeof ListItem>, 'button'> & {
  to?: LinkProps['to']
  contest?: IContest
  onClaim?: (contestId: string, email?: string) => void
}

const styles = ({ palette, spacing }: Theme) => createStyles({
  root: {
    padding: 0,
  },
  coverBackground: {
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '50%',
  },
  emailPromptButton: {
    backgroundColor: palette.primary.main,
    marginLeft: spacing(2),

    '&:focus, &:hover': {
      backgroundColor: palette.primary.main,
    },
  },
  emailPromptTextLabel: {
    color: palette.secondary.main,
  },
  emailPromptTextInput: {
    color: palette.secondary.main,
    borderBottom: `1px solid ${palette.secondary.main}`
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

const Event: React.SFC<InjectedProps> = ({ contest, onClaim, classes, className, ...props }) => {
  const [shouldDisplayEmailField, setShouldDisplayEmailField] = useState(false)
  const [email, setEmail] = useState('')

  if (!contest) {
    return null
  }

  const ribbon = contest.goal && contest.status !== ContestStatus.CS_OVER ? {
    text: <T id={ContestGoalTypeToken(contest.goal.type!)} values={{ count: contest.goal.x }} />,
    color: contest.status === ContestStatus.CS_UPCOMING ? 'nile' : 'royal',
  } as RibbonProps : undefined

  const isEmailRequiredToClaimPrize = (contest.ownEntry?.prize || []).some(prize => !!prize.product)

  return (
    <ListItem
      divider
      disableGutters
      component={EventLink as any}
      className={classNames(className, classes.root)}
      {...props}
    >
      <Grid direction="column" wrap="nowrap" container>
        <Grid item>
          <EventDetailsHeader
            color="accent"
            title={contest.title!}
            ribbon={ribbon}
            className={classes.coverBackground}
            style={{ backgroundImage: `url(${contest.bannerUrl})` }}
          />
        </Grid>
        <Grid item>
          <Paper pad="small" color="accent">
            <Grid direction="row" wrap="nowrap" justify="space-around" container>
              {contest.status === ContestStatus.CS_UPCOMING && (
                <>
                  <CountdownLabel labelKey="contests_timeuntilstart" time={contest.startTime} />
                  {!isEmpty(contest.topPrize) && (
                    <TopPrizeLabel topPrize={contest.topPrize} />
                  )}
                </>
              )}

              {contest.status === ContestStatus.CS_LIVE && (
                <>
                  <CountdownLabel labelKey="contests_timeuntilend" time={contest.endTime} />
                  {!isEmpty(contest.ownEntry) && (
                    <>
                      <RankLabel rank={contest.ownEntry!.rank} />
                      <ScoreLabel score={contest.ownEntry!.score} />
                    </>
                  )}
                  {isEmpty(contest.ownEntry) && !isEmpty(contest.topPrize) && (
                    <TopPrizeLabel topPrize={contest.topPrize} />
                  )}
                </>
              )}

              {contest.status === ContestStatus.CS_OVER && (
                <>
                  {contest.unclaimedPrize && onClaim && !shouldDisplayEmailField && (
                    <Button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        if (isEmailRequiredToClaimPrize) {
                          setShouldDisplayEmailField(true)
                        } else {
                          onClaim(contest.contestId!)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      <T id="contests_prize_claim" />
                    </Button>
                  )}

                  {contest.unclaimedPrize && onClaim && shouldDisplayEmailField && (
                    <Grid
                      xs
                      container
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onMouseDown={e => {
                        e.stopPropagation()
                      }}
                    >
                      <Grid xs>
                        <TextField
                          fullWidth
                          autoFocus
                          type="email"
                          value={email}
                          color="secondary"
                          onChange={e => setEmail(e.target.value)}
                          label={<T id="contests_prize_emailprompt_title" />}
                          InputProps={{
                            disableUnderline: true,
                            classes: {
                              root: classes.emailPromptTextInput,
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                            classes: {
                              root: classes.emailPromptTextLabel,
                            },
                          }}
                        />
                      </Grid>
                      <Grid>
                        <IconButton
                          onClick={() => {
                            if (isEmail(email)) {
                              onClaim(contest.contestId!, email)
                            }
                          }}
                          classes={{ root: classes.emailPromptButton }}
                        >
                          <ArrowRightAltIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}

                  {!contest.unclaimedPrize && (
                    <>
                      <FinishedLabel endTime={contest.endTime} />
                      {!isEmpty(contest.ownEntry) && (
                        <>
                          <RankLabel rank={contest.ownEntry!.rank} />
                          <PrizeLabel prize={contest.ownEntry!.prize} />
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </ListItem>
  )
}

Event.defaultProps = {
  contest: {},
}

export default withStyles(styles)(Event)
